/**
 * DeleteAccountPage — `/delete-account`
 *
 * Public, login-free-to-load page required by Google Play's Data Safety
 * section: any app that supports account creation must offer a web page
 * (reachable without installing the app) where a user can authenticate
 * and permanently delete their account and associated data.
 *
 * Flow: sign in with username/password → confirm → DELETE /api/auth/me/.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/context/AuthContext'
import { login as apiLogin, deleteAccount } from '@/api/auth'
import AuthShell, { AuthInput } from '@/pages/Auth/AuthShell'
import { CheckCircle2 } from 'lucide-react'

export default function DeleteAccountPage() {
  const { t } = useTranslation()
  const { login, logout, user, isAuth } = useAuth()

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [error, setError]        = useState(null)
  const [loading, setLoading]    = useState(false)
  const [done, setDone]          = useState(false)

  async function handleLogin(e) {
    e.preventDefault()
    if (!username || !password) return
    setLoading(true); setError(null)
    try {
      const { data } = await apiLogin(username, password)
      login({ access: data.access, refresh: data.refresh, user: data.user })
    } catch (err) {
      setError(err.response?.data?.detail ?? t('deleteAccount.loginFailed', 'Kirish amalga oshmadi. Login yoki parolni tekshiring.'))
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete() {
    setLoading(true); setError(null)
    try {
      await deleteAccount()
      logout()
      setDone(true)
    } catch (err) {
      setError(err.response?.data?.detail ?? t('deleteAccount.deleteFailed', "Hisobni o'chirib bo'lmadi. Birozdan so'ng qayta urinib ko'ring."))
    } finally {
      setLoading(false)
    }
  }

  if (done) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center" style={{ gap: 12 }}>
          <CheckCircle2 size={40} color="var(--mint)" />
          <h2 className="fw-7 t-xl" style={{ color: 'var(--ink)' }}>
            {t('deleteAccount.doneTitle', "Hisobingiz o'chirildi")}
          </h2>
          <p className="t-sm t-mut">
            {t('deleteAccount.doneBody', "Hisobingiz va u bilan bog'liq barcha ma'lumotlar tizimdan butunlay o'chirildi.")}
          </p>
          <Link to="/" style={{ color: 'var(--coral)', fontWeight: 600, textDecoration: 'none', marginTop: 8 }}>
            {t('deleteAccount.backHome', 'Bosh sahifaga qaytish')}
          </Link>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <h2 className="fw-7 t-xl" style={{ color: 'var(--ink)', marginBottom: 6 }}>
        {t('deleteAccount.title', "Hisobni o'chirish")}
      </h2>
      <p className="t-sm t-mut" style={{ marginBottom: 24 }}>
        {t('deleteAccount.subtitle', "Hisobingizni va unga tegishli barcha ma'lumotlarni (imtihon natijalari, javoblar, uy vazifalari) butunlay o'chirib tashlaysiz. Bu amalni ortga qaytarib bo'lmaydi.")}
      </p>

      {error && (
        <div className="t-sm" style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 12,
          background: 'var(--danger-tint)', border: '1px solid var(--danger)', color: 'var(--danger)',
        }}>
          {error}
        </div>
      )}

      {!isAuth ? (
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <AuthInput
            label={t('auth.username', 'Foydalanuvchi nomi')}
            value={username} onChange={(e) => setUsername(e.target.value)}
            autoComplete="username" required
          />
          <AuthInput
            label={t('auth.password', 'Parol')}
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password" required
          />
          <button
            type="submit" disabled={loading}
            className="btn btn-primary" style={{ width: '100%', marginTop: 4, justifyContent: 'center' }}
          >
            {loading ? t('common.loading', 'Yuklanmoqda…') : t('deleteAccount.continue', 'Davom etish')}
          </button>
        </form>
      ) : (
        <div className="flex flex-col" style={{ gap: 16 }}>
          <div className="t-sm" style={{
            padding: '12px 16px', borderRadius: 12,
            background: 'var(--surface-2)', border: '1px solid var(--border)',
          }}>
            {t('deleteAccount.signedInAs', 'Hisob')}: <strong>{user?.full_name || user?.username}</strong>
          </div>

          <label className="row t-sm" style={{ gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              style={{ marginTop: 2 }}
            />
            <span>
              {t('deleteAccount.confirmLabel', "Hisobim va unga tegishli barcha ma'lumotlar butunlay o'chirilishini tushunaman va tasdiqlayman.")}
            </span>
          </label>

          <button
            type="button" disabled={!confirmed || loading}
            onClick={handleDelete}
            className="btn" style={{
              width: '100%', justifyContent: 'center',
              background: 'var(--danger)', color: '#fff',
              opacity: !confirmed || loading ? 0.5 : 1,
            }}
          >
            {loading
              ? t('common.loading', 'Yuklanmoqda…')
              : t('deleteAccount.deleteButton', "Hisobni butunlay o'chirish")}
          </button>
        </div>
      )}
    </AuthShell>
  )
}
